import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { getDocs, addDoc, collection, doc, updateDoc, deleteDoc, writeBatch, query, where, arrayUnion, arrayRemove } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import Header from '../../Header';
import DirectoryCard from '../DirectoryCard';
import NewFolderInput from '../NewFolderInput';
import { db } from '../../../firebase';
import DirectoryUsersModal from './DirectoryUsersModal';

import './styles.css';

function Directories() {
	const [nameFilter, setNameFilter] = useState('');
	const [loading, setLoading] = useState(true);
	const [directories, setDirectories] = useState([]);
	const [showNewFolderInput, setShowNewFolderInput] = useState(false);
	const [projectsExist, setProjectsExist] = useState(false);
	const [moveSnackbar, setMoveSnackbar] = useState({ open: false, message: '', severity: 'success' });
	const [selectedDirectory, setSelectedDirectory] = useState(null);
	const [showDirectoryUsers, setShowDirectoryUsers] = useState(false);

	const { organizationId } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		fetchDirectories();
	}, []);

	const fetchDirectories = async () => {
		setLoading(true);
		try {
			const directoriesRef = collection(db, 'organizations', organizationId, 'directories');
			const querySnapshot = await getDocs(directoriesRef);
			const directories = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setDirectories(directories);
		} finally {
			setLoading(false);
		}
	};

	const hideNewFolderInput = () => {
		setShowNewFolderInput(false);
	};

	const onBackClick = () => {
		navigate('/admin/organizations');
	};

	const handleClose = () => {
		setProjectsExist(false);
	};

	const handleShareDirectory = async (directory) => {
		try {
			const projectsSnapshot = await getDocs(
				query(collection(db, 'projects'), where('directoryId', '==', directory.id))
			);
			if (projectsSnapshot.empty) {
				setMoveSnackbar({ open: true, message: `"${directory.name}" has no projects. Add projects before sharing.`, severity: 'warning' });
				return;
			}
		} catch (error) {
			console.error('Error checking projects for share:', error);
		}
		setSelectedDirectory(directory);
		setShowDirectoryUsers(true);
	};

	const toggleDirectoryUsers = () => {
		setShowDirectoryUsers((prev) => !prev);
		if (showDirectoryUsers) setSelectedDirectory(null);
	};

	const renameDirectory = async (directory, newName) => {
		if (!newName.trim()) return;
		const directoryRef = doc(db, 'organizations', organizationId, 'directories', directory.id);
		try {
			await updateDoc(directoryRef, { name: newName.trim() });
			fetchDirectories();
		} catch (error) {
			console.error('Error renaming directory: ', error);
		}
	};

	const moveDirectory = async (directory, targetOrg) => {
		try {
			// 1. Copy directory to target organization, get new directory ID
			const { id: oldDirId, ...directoryData } = directory;
			const newDirRef = await addDoc(
				collection(db, 'organizations', targetOrg.id, 'directories'),
				directoryData
			);
			const newDirId = newDirRef.id;

			// 2. Update all projects (sections) that belong to this directory
			const projectsSnapshot = await getDocs(
				query(collection(db, 'projects'), where('directoryId', '==', oldDirId))
			);
			const batch = writeBatch(db);
			projectsSnapshot.docs.forEach((projectDoc) => {
				batch.update(projectDoc.ref, {
					directoryId: newDirId,
					organizationId: targetOrg.id,
				});
			});
			await batch.commit();

			// 3. Migrate userAccess docs: swap oldDirId → newDirId, add targetOrg to organizationIds
			const userAccessSnapshot = await getDocs(
				query(collection(db, 'userAccess'), where('directoryIds', 'array-contains', oldDirId))
			);
			if (!userAccessSnapshot.empty) {
				const accessBatch = writeBatch(db);
				userAccessSnapshot.docs.forEach((userDoc) => {
					accessBatch.update(userDoc.ref, {
						directoryIds: arrayUnion(newDirId),
						organizationIds: arrayUnion(targetOrg.id),
					});
				});
				await accessBatch.commit();
				// Remove old directoryId in a second batch (arrayUnion and arrayRemove can't be in same update)
				const cleanupBatch = writeBatch(db);
				userAccessSnapshot.docs.forEach((userDoc) => {
					cleanupBatch.update(userDoc.ref, { directoryIds: arrayRemove(oldDirId) });
				});
				await cleanupBatch.commit();
			}

			// 4. Delete original directory doc
			await deleteDoc(doc(db, 'organizations', organizationId, 'directories', oldDirId));

			setMoveSnackbar({ open: true, message: `Moved "${directory.name}" to "${targetOrg.name}"`, severity: 'success' });
			fetchDirectories();
		} catch (error) {
			console.error('Error moving directory: ', error);
			setMoveSnackbar({ open: true, message: 'Failed to move project. Please try again.', severity: 'error' });
		}
	};

	const filtered = directories.filter((d) =>
		d.name.toLowerCase().includes(nameFilter.toLowerCase())
	);

	return (
		<div className="dirs__root">
			<Header />
			<div className="dirs__content">
				{/* Back navigation */}
				<button className="dirs__back-btn" onClick={onBackClick}>
					<ArrowBackIcon fontSize="small" />
					Organizations
				</button>

				{/* Page header */}
				<div className="dirs__header">
					<div>
						<h1 className="dirs__title">Projects</h1>
						<p className="dirs__subtitle">Manage projects for this organization</p>
					</div>
				</div>

				{/* Toolbar */}
				<div className="dirs__toolbar">
					<div className="dirs__search-wrap">
						<SearchIcon className="dirs__search-icon" fontSize="small" />
						<input
							className="dirs__search-input"
						placeholder="Search projects…"
							value={nameFilter}
							onChange={(e) => setNameFilter(e.target.value)}
						/>
					</div>
					<div className="dirs__toolbar-actions">
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={() => setShowNewFolderInput(true)}
							disabled={showNewFolderInput}
							sx={{
								background: '#0f2d4e',
								fontFamily: '"Open Sans", sans-serif',
								fontWeight: 600,
								textTransform: 'none',
								borderRadius: '8px',
								'&:hover': { background: '#1a4a7a' },
								'&.Mui-disabled': { background: 'rgba(15,45,78,0.2)', color: '#fff' },
							}}
						>
							Add Project
						</Button>
					</div>
				</div>

				{/* Results count */}
				{directories.length > 0 && (
					<p className="dirs__results-label">
						{filtered.length} {filtered.length === 1 ? 'project' : 'projects'}
						{nameFilter ? ` matching "${nameFilter}"` : ''}
					</p>
				)}

				{/* Grid */}
				<div className="dirs__grid">
					{showNewFolderInput && (
						<NewFolderInput
							fetchDirectories={fetchDirectories}
							organizationId={organizationId}
							hideNewFolderInput={hideNewFolderInput}
						/>
					)}
					{loading
						? Array.from({ length: 5 }).map((_, i) => (
							<div key={i} className="dirs__skeleton-card">
								<div className="dirs__skeleton-icon admin-skeleton" />
								<div className="dirs__skeleton-name admin-skeleton" />
							</div>
						))
						: (
							<>
								{filtered.map((directory) => (
									<DirectoryCard
										key={directory.id}
										directory={directory}
										onRename={renameDirectory}
									onMove={moveDirectory}									onShare={handleShareDirectory}									/>
								))}
								{!showNewFolderInput && filtered.length === 0 && (
									<div className="dirs__empty">
										<FolderOpenIcon sx={{ fontSize: 48, color: 'rgba(15,45,78,0.2)', mb: 1 }} />
										<p className="dirs__empty-title">
											{nameFilter ? 'No projects match your search' : 'No projects yet'}
										</p>
										{!nameFilter && (
											<p className="dirs__empty-hint">Click "Add Project" to create your first project.</p>
										)}
									</div>
								)}
							</>
						)
					}
				</div>
				<Snackbar
					open={projectsExist}
					autoHideDuration={6000}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: '100%' }}>
						Cannot delete project. Sections exist inside it.
					</Alert>
				</Snackbar>
				<Snackbar
					open={moveSnackbar.open}
					autoHideDuration={4000}
					onClose={() => setMoveSnackbar((s) => ({ ...s, open: false }))}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<Alert
						onClose={() => setMoveSnackbar((s) => ({ ...s, open: false }))}
						severity={moveSnackbar.severity}
						variant="filled"
						sx={{ width: '100%' }}
					>
						{moveSnackbar.message}
					</Alert>
				</Snackbar>
				<DirectoryUsersModal
					open={showDirectoryUsers}
					toggleModal={toggleDirectoryUsers}
					directoryId={selectedDirectory?.id}
					directoryName={selectedDirectory?.name}
					organizationId={organizationId}
				/>
			</div>
		</div>
	);
}

export default Directories;
